// src/api/providerAssignmentsApi.ts
//
// RTK Query endpoints for the CRM Provider Assignment domain.
// Backend contract: server/src/routes/providerAssignments.ts +
// server/src/routes/providerRoles.ts.
//
// Tag strategy:
//   ProviderAssignment/LIST                 — all-list caches
//   ProviderAssignment/{id}                 — single assignment
//   ProviderAssignment/{id}-history         — history rows for an assignment
//   ProviderAssignment/contact-{contactId}  — Contact-scoped assignment list
//   ProviderAssignment/provider-{userId}    — Provider-scoped assignment list
//   ProviderAssignment/dept-{circleId}      — Department-scoped assignment list
//   ProviderAssignment/workload-{userId}    — Provider workload counters
//   ProviderRole/LIST                       — role catalog
//   ProviderRole/{id}
//
// The realtime hook (useProviderAssignmentRealtime) invalidates these
// tags on PROVIDER_ASSIGNMENT_* events.

import { baseApi } from './baseApi';
import type {
  CreateAssignmentRequest,
  CreateProviderRoleRequest,
  EndAssignmentRequest,
  ListAssignmentsParams,
  PersonProviderAssignment,
  PersonProviderAssignmentHistory,
  ProviderRoleDefinition,
  ProviderWorkload,
  ReassignAssignmentRequest,
  ReassignAssignmentResponse,
  UpdateAssignmentRequest,
  UpdateProviderRoleRequest,
} from '../types/api';

const LIST_ID = 'LIST' as const;

function assignmentTagsFor(row: PersonProviderAssignment) {
  return [
    { type: 'ProviderAssignment' as const, id: row.id },
    { type: 'ProviderAssignment' as const, id: LIST_ID },
    { type: 'ProviderAssignment' as const, id: `contact-${row.contactId}` },
    { type: 'ProviderAssignment' as const, id: `provider-${row.providerUserId}` },
    { type: 'ProviderAssignment' as const, id: `dept-${row.departmentCircleId}` },
    { type: 'ProviderAssignment' as const, id: `workload-${row.providerUserId}` },
    { type: 'ProviderAssignment' as const, id: `${row.id}-history` },
  ];
}

export const providerAssignmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Role catalog ------------------------------------------------
    getProviderRoles: builder.query<ProviderRoleDefinition[], { includeInactive?: boolean } | void>({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.includeInactive) s.append('includeInactive', '1');
        const qs = s.toString();
        return `/provider-roles${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result) =>
        result
          ? [
              { type: 'ProviderRole' as const, id: LIST_ID },
              ...result.map((r) => ({ type: 'ProviderRole' as const, id: r.id })),
            ]
          : [{ type: 'ProviderRole' as const, id: LIST_ID }],
    }),

    createProviderRole: builder.mutation<ProviderRoleDefinition, CreateProviderRoleRequest>({
      query: (body) => ({ url: '/provider-roles', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProviderRole', id: LIST_ID }],
    }),

    updateProviderRole: builder.mutation<
      ProviderRoleDefinition,
      { id: string; data: UpdateProviderRoleRequest }
    >({
      query: ({ id, data }) => ({ url: `/provider-roles/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProviderRole', id: arg.id },
        { type: 'ProviderRole', id: LIST_ID },
      ],
    }),

    deleteProviderRole: builder.mutation<void, string>({
      query: (id) => ({ url: `/provider-roles/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProviderRole', id },
        { type: 'ProviderRole', id: LIST_ID },
      ],
    }),

    // ---- Assignments -------------------------------------------------
    listProviderAssignments: builder.query<PersonProviderAssignment[], ListAssignmentsParams | void>({
      query: (params) => {
        const s = new URLSearchParams();
        if (params?.workspaceId) s.append('workspaceId', params.workspaceId);
        if (params?.contactId) s.append('contactId', params.contactId);
        if (params?.providerUserId) s.append('providerUserId', params.providerUserId);
        if (params?.departmentCircleId) s.append('departmentCircleId', params.departmentCircleId);
        if (params?.providerRoleId) s.append('providerRoleId', params.providerRoleId);
        if (params?.priority) s.append('priority', params.priority);
        if (params?.status) s.append('status', params.status);
        if (params?.activeOnly) s.append('activeOnly', '1');
        if (params?.limit != null) s.append('limit', String(params.limit));
        if (params?.offset != null) s.append('offset', String(params.offset));
        const qs = s.toString();
        return `/provider-assignments${qs ? `?${qs}` : ''}`;
      },
      providesTags: (result, _e, params) => {
        const base: Array<{ type: 'ProviderAssignment'; id: string }> = [
          { type: 'ProviderAssignment', id: LIST_ID },
        ];
        if (params?.contactId) base.push({ type: 'ProviderAssignment', id: `contact-${params.contactId}` });
        if (params?.providerUserId) base.push({ type: 'ProviderAssignment', id: `provider-${params.providerUserId}` });
        if (params?.departmentCircleId) base.push({ type: 'ProviderAssignment', id: `dept-${params.departmentCircleId}` });
        if (result) {
          for (const r of result) {
            base.push({ type: 'ProviderAssignment', id: r.id });
          }
        }
        return base;
      },
    }),

    getProviderAssignment: builder.query<PersonProviderAssignment, string>({
      query: (id) => `/provider-assignments/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ProviderAssignment' as const, id }],
    }),

    getProviderAssignmentHistory: builder.query<PersonProviderAssignmentHistory[], string>({
      query: (id) => `/provider-assignments/${id}/history`,
      providesTags: (_r, _e, id) => [{ type: 'ProviderAssignment' as const, id: `${id}-history` }],
    }),

    getProviderWorkload: builder.query<ProviderWorkload, string>({
      query: (providerUserId) => `/provider-assignments/workload/${providerUserId}`,
      providesTags: (_r, _e, providerUserId) => [
        { type: 'ProviderAssignment' as const, id: `workload-${providerUserId}` },
      ],
    }),

    createProviderAssignment: builder.mutation<PersonProviderAssignment, CreateAssignmentRequest>({
      query: (body) => ({ url: '/provider-assignments', method: 'POST', body }),
      invalidatesTags: (row) => (row ? assignmentTagsFor(row) : [{ type: 'ProviderAssignment', id: LIST_ID }]),
    }),

    updateProviderAssignment: builder.mutation<
      PersonProviderAssignment,
      { id: string; data: UpdateAssignmentRequest }
    >({
      query: ({ id, data }) => ({ url: `/provider-assignments/${id}`, method: 'PATCH', body: data }),
      invalidatesTags: (row, _e, arg) => {
        if (row) return assignmentTagsFor(row);
        return [
          { type: 'ProviderAssignment', id: arg.id },
          { type: 'ProviderAssignment', id: LIST_ID },
          { type: 'ProviderAssignment', id: `${arg.id}-history` },
        ];
      },
    }),

    reassignProviderAssignment: builder.mutation<
      ReassignAssignmentResponse,
      { id: string; data: ReassignAssignmentRequest }
    >({
      query: ({ id, data }) => ({ url: `/provider-assignments/${id}/reassign`, method: 'POST', body: data }),
      invalidatesTags: (res, _e, arg) => {
        const tags: any[] = [
          { type: 'ProviderAssignment', id: arg.id },
          { type: 'ProviderAssignment', id: LIST_ID },
          { type: 'ProviderAssignment', id: `${arg.id}-history` },
        ];
        if (res) {
          tags.push(
            ...assignmentTagsFor(res.previous),
            ...assignmentTagsFor(res.next),
          );
        }
        return tags;
      },
    }),

    endProviderAssignment: builder.mutation<
      PersonProviderAssignment,
      { id: string; data: EndAssignmentRequest }
    >({
      query: ({ id, data }) => ({ url: `/provider-assignments/${id}/end`, method: 'POST', body: data }),
      invalidatesTags: (row, _e, arg) => {
        if (row) return assignmentTagsFor(row);
        return [
          { type: 'ProviderAssignment', id: arg.id },
          { type: 'ProviderAssignment', id: LIST_ID },
          { type: 'ProviderAssignment', id: `${arg.id}-history` },
        ];
      },
    }),
  }),
});

export const {
  useGetProviderRolesQuery,
  useCreateProviderRoleMutation,
  useUpdateProviderRoleMutation,
  useDeleteProviderRoleMutation,
  useListProviderAssignmentsQuery,
  useGetProviderAssignmentQuery,
  useGetProviderAssignmentHistoryQuery,
  useGetProviderWorkloadQuery,
  useCreateProviderAssignmentMutation,
  useUpdateProviderAssignmentMutation,
  useReassignProviderAssignmentMutation,
  useEndProviderAssignmentMutation,
} = providerAssignmentsApi;
